import { Check, Search, Shield, X } from "lucide-react";
import { useDeferredValue, useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { AccessRole } from "../../types/access.types";

export interface RolePickerListProps {
  roles: AccessRole[];
  /** Currently picked role ids. The parent owns the set — this component only reports changes. */
  selected: string[];
  onChange: (roleIds: string[]) => void;
  disabled?: boolean;
  /** Rows rendered before the list scrolls. @default 7 */
  visibleRows?: number;
}

/**
 * Searchable multi-select over the RBAC roles catalog.
 *
 * Selection is the parent's state, not this component's: the invite drawer submits it with the invitation, so
 * a pick here must not imply a save. That is the difference from `AssignRolesDrawer`, which auto-saves every
 * toggle against an existing principal and therefore cannot be reused for a person who does not exist yet.
 *
 * Search covers name, slug and description because that is how the catalog is actually recognised — an admin
 * looks for "payments" and the role is `payments_write`, named "Payments Write". Filtering is local: the
 * whole catalog is already in memory (`AccessDataProvider` loads it once for the section), and `GET
 * /internal/roles` accepts no search param anyway.
 */
export function RolePickerList({
  roles,
  selected,
  onChange,
  disabled,
  visibleRows = 7,
}: RolePickerListProps) {
  const { t } = useTranslations("settings");
  const [term, setTerm] = useState("");
  // The catalog is ~90 rows and every keystroke re-filters it; deferring keeps typing smooth.
  const deferredTerm = useDeferredValue(term);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  /**
   * The selection the row ORDER is computed from, which is deliberately NOT always the live one.
   *
   * Ordering against the live selection reshuffled the list on every click: the row just ticked jumped to the
   * top, the focused button went with it, and the browser dragged the scroll container along to keep that
   * focus visible — throwing the reader to the top of the list exactly while they were working through
   * several roles in a row.
   *
   * So the order follows the selection only until the reader ticks something themselves. That keeps the
   * drawer's pre-ticked defaults at the top when the picker opens (they arrive in an effect, after the first
   * render), and freezes the order the moment it would start moving under the reader's cursor.
   */
  const [orderingSeed, setOrderingSeed] = useState(selectedSet);
  const [followsSelection, setFollowsSelection] = useState(true);
  const [seedTerm, setSeedTerm] = useState(deferredTerm);

  // A new filter re-seeds: putting picked roles first is what keeps a chosen role visible under a filter,
  // and the list has already re-rendered wholesale, so there is no stable row position to protect.
  if (seedTerm !== deferredTerm) {
    setSeedTerm(deferredTerm);
    setOrderingSeed(selectedSet);
    setFollowsSelection(true);
  }

  const seed = followsSelection ? selectedSet : orderingSeed;

  const filtered = useMemo(() => {
    const needle = deferredTerm.trim().toLowerCase();
    const matches = needle
      ? roles.filter((role) =>
          [role.name, role.slug, role.description ?? ""].some((field) =>
            field.toLowerCase().includes(needle),
          ),
        )
      : roles;
    return [...matches].sort((a, b) => {
      const pickedDelta = Number(seed.has(b.id)) - Number(seed.has(a.id));
      return pickedDelta !== 0 ? pickedDelta : a.name.localeCompare(b.name);
    });
  }, [roles, deferredTerm, seed]);

  const toggle = (roleId: string) => {
    if (disabled) return;
    // Freeze the order the reader is currently looking at, so the row they just clicked stays put.
    if (followsSelection) {
      setOrderingSeed(selectedSet);
      setFollowsSelection(false);
    }
    const next = new Set(selectedSet);
    if (next.has(roleId)) next.delete(roleId);
    else next.add(roleId);
    onChange([...next]);
  };

  /** Clearing is a deliberate bulk action, so the order may settle again — nothing is picked to sort first. */
  const clearAll = () => {
    if (disabled) return;
    setOrderingSeed(new Set());
    setFollowsSelection(true);
    onChange([]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <ColocaInput
            value={term}
            onValueChange={setTerm}
            disabled={disabled}
            placeholder={t("access_invite_roles_search")}
            className="pl-8"
            aria-label={t("access_invite_roles_search")}
          />
        </div>
        {selected.length > 0 && (
          <ColocaButton
            type="button"
            variant="ghost"
            size="xs"
            icon={X}
            disabled={disabled}
            onClick={clearAll}
          >
            {t("access_invite_roles_clear")}
          </ColocaButton>
        )}
      </div>

      <div
        className="overflow-y-auto rounded-md border border-border"
        style={{ maxHeight: `${visibleRows * 44}px` }}
      >
        {filtered.length === 0 ? (
          <div className="p-3">
            <ColocaEmpty surface="card" icon={Search} title={t("access_invite_roles_no_matches")} />
          </div>
        ) : (
          <ul className="m-0 list-none divide-y divide-border/60 p-0">
            {filtered.map((role) => {
              const isPicked = selectedSet.has(role.id);
              return (
                <li key={role.id}>
                  <button
                    type="button"
                    disabled={disabled}
                    aria-pressed={isPicked}
                    onClick={() => toggle(role.id)}
                    className={cn(
                      "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                      "hover:bg-foreground/5 disabled:cursor-not-allowed disabled:opacity-60",
                      isPicked && "bg-primary/5",
                    )}
                  >
                    <span
                      className={cn(
                        "grid h-[18px] w-[18px] shrink-0 place-items-center rounded border",
                        isPicked
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card",
                      )}
                    >
                      {isPicked && <Check size={12} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium text-foreground">
                        {role.name}
                      </span>
                      <span className="block truncate font-mono text-[11px] text-muted-foreground">
                        {role.slug}
                      </span>
                    </span>
                    {role.organization_id == null && (
                      <ColocaBadge variant="secondary" className="shrink-0 text-[10px]">
                        {t("access_invite_roles_global")}
                      </ColocaBadge>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
        <Shield size={12} className="shrink-0" />
        {selected.length === 0
          ? t("access_invite_roles_none_selected")
          : t("access_invite_roles_count", { count: selected.length })}
      </p>
    </div>
  );
}
